/**
 * BondSkillEffect — Conjuration trunk passive: summon contribution XP credit.
 *
 * Always-on once unlocked. Kills where any summon dealt at least 1 point of
 * damage earn full Conjuration XP (1.0×) instead of a reduced fraction
 * (pre-Bond baseline: 0.5×).
 *
 * Mechanic:
 * - Tracks per-enemy summon damage contributions in a Set<string> of enemyIds.
 * - When any summon deals damage to an enemy, `recordSummonDamage(enemyId)` is
 *   called (by the combat resolution layer).
 * - On enemy death, `getXpMultiplier(enemyId)` returns 1.0 if any summon
 *   contributed, or the pre-Bond baseline fraction if not.
 * - After the kill is processed, `clearContribution(enemyId)` removes the
 *   tracking entry.
 *
 * Tree-wide: applies to wisp, familiar, shade, and any future Conjuration summon
 * archetypes. The caller is responsible for routing all summon damage through
 * `recordSummonDamage`.
 *
 * Resolver key: `summonContributionXpMultiplier` with max aggregation via
 * `getTreePassiveEffect('conjuration', 'summonContributionXpMultiplier', 'max')`.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Bond
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BondDeps {
  readonly resolver: {
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface BondConfig {
  /**
   * XP fraction applied to kills where NO summon contributed.
   * Bond does not change this — it only restores full (1.0×) credit for
   * summon-contributed kills.
   */
  readonly preBondBaselineFraction: number;
}

const DEFAULT_CONFIG: BondConfig = {
  preBondBaselineFraction: 0.5,  // design-decisions.md: recommend 0.5× as default
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BondSkillEffect implements SkillEffect {
  readonly skillId = 'bond';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BondDeps;
  private readonly config: BondConfig;

  /** Set of enemy IDs that received at least 1 damage from any summon. */
  private readonly _summonDamageContributions = new Set<string>();

  constructor(deps: BondDeps, config: Partial<BondConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Bond-specific API (called by combat/XP resolution layer) ---

  /**
   * Record that a summon dealt damage to the given enemy.
   * Persists until `clearContribution(enemyId)` is called.
   */
  recordSummonDamage(enemyId: string): void {
    this._summonDamageContributions.add(enemyId);
  }

  /**
   * Get the XP multiplier to apply when this enemy is killed.
   * Returns 1.0 if any summon contributed, preBondBaselineFraction otherwise.
   */
  getXpMultiplier(enemyId: string): number {
    if (this._summonDamageContributions.has(enemyId)) {
      return this.deps.resolver.getTreePassiveEffect(
        'conjuration', 'summonContributionXpMultiplier', 'max',
      );
    }
    return this.config.preBondBaselineFraction;
  }

  /**
   * Clear the contribution tracking for an enemy after their kill is processed.
   */
  clearContribution(enemyId: string): void {
    this._summonDamageContributions.delete(enemyId);
  }

  /** Number of enemies currently tracked as having received summon damage. */
  get trackedCount(): number {
    return this._summonDamageContributions.size;
  }

  /** Whether the given enemy has a recorded summon contribution. */
  hasContribution(enemyId: string): boolean {
    return this._summonDamageContributions.has(enemyId);
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — always on, never manually activated
  }

  activate(): boolean {
    return false; // passive — no activation
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — no frame logic needed
  }

  destroy(): void {
    this._summonDamageContributions.clear();
  }
}
